import { useCallback, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Post content is injected as raw HTML, so cross-reference links between posts
 * are plain anchors and would otherwise reload the whole app. This routes them
 * through the router instead, leaving external links and in-page anchors alone.
 */
export function useInternalLinkRouting() {
  const navigate = useNavigate();

  return useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (event.defaultPrevented || event.button !== 0) return;
      // Modified clicks mean "open in a new tab/window" — leave them to the browser.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = (event.target as HTMLElement).closest('a');
      if (!anchor) return;
      if (anchor.hasAttribute('download')) return;
      if (anchor.target && anchor.target !== '_self') return;

      const href = anchor.getAttribute('href');
      if (!href) return;
      // Table-of-contents links scroll the page natively.
      if (href.startsWith('#')) return;

      const url = new URL(href, window.location.href);
      if (url.origin !== window.location.origin) return;

      event.preventDefault();
      navigate(url.pathname + url.search + url.hash);
    },
    [navigate],
  );
}
